// Final App.jsx with Clean Header Design + Category and Initial Effort Estimations
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Papa from 'papaparse';
import Sidebar from './components/Sidebar';
import SprintBoard from './components/SprintBoard';
import SearchBar from './components/SearchBar';
import LoadingSpinner from './components/LoadingSpinner';
import AddBoardButton from './components/AddBoardButton';
import DraggableBoardHeader from './components/DraggableBoardHeader';
import ViewModeToggle from './components/ViewModeToggle';
import UserMenu from './components/UserMenu';
// Real-time collaboration imports
import { useRealTime } from './hooks/useRealTime';
import UserPresence from './components/UserPresence';
import ActivityFeed from './components/ActivityFeed';
import CollaborativeCursors from './components/CollaborativeCursors';
import LiveNotifications from './components/LiveNotifications';
// Other imports
import { supabase } from './supabaseClient';
import Login from './Login';
import { COLORS } from './constants';
import { useBoards } from './hooks/useBoards';
import useBoardFocus from './hooks/useBoardFocus';
import './App.css';

function App() {
  const [session, setSession] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const initializingRef = useRef(false);
  const initializedRef = useRef(false);
  const boardsRef = useRef(null);
  const containerRef = useRef(null);
  const cursorTimeoutRef = useRef(null);

  // Board drag & drop state
  const [draggedBoard, setDraggedBoard] = useState(null);
  const [dragOverBoard, setDragOverBoard] = useState(null);

  // Real-time collaboration state
  const [activityFeedOpen, setActivityFeedOpen] = useState(false);

  // Use the boards hook
  const {
    boards,
    loading: boardsLoading,
    error: boardsError,
    viewMode,
    fetchBoards,
    createBoard,
    updateBoard,
    updateBoardStatus,
    deleteBoard,
    reorderBoards,
    switchViewMode,
    loadUserPreferences,
    getBoardById,
    getBoardByName,
    canEditBoard
  } = useBoards();

  // Use the board focus hook for auto-focusing "In Progress" boards
  const { focusBoard } = useBoardFocus(boardsRef, boards, loading);

  // Use the real-time hook
  const {
    onlineUsers,
    recentActivities,
    isConnected,
    initializeRealTime,
    updateCursorPosition,
    logActivity,
    goOffline
  } = useRealTime();

  const fetchTickets = useCallback(async () => {
    try {
      setError(null);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Join with boards to get board information
      let query = supabase
        .from('tickets')
        .select(`
          *,
          board:boards(id, name, status, color)
        `);

      // In private mode, only show user's tickets
      // In collaborative mode, show ALL tickets
      if (viewMode === 'private') {
        query = query.eq('user_id', user.id);
      }
      
      const { data, error: fetchError } = await query;
      
      if (fetchError) throw fetchError;
      setTickets(data || []);
      
      console.log(`🎫 Tickets fetched (${viewMode} mode):`, data?.length || 0);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError('Failed to fetch tickets: ' + err.message);
    }
  }, [viewMode]);

  // Auth initialization
  useEffect(() => {
    if (initializingRef.current || initializedRef.current) {
      return;
    }

    initializingRef.current = true;
    
    const initSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) throw error;
        
        setSession(session);
        
        if (session) {
          await Promise.all([
            fetchTickets(),
            fetchBoards(),
            loadUserPreferences()
          ]);
        }
      } catch (err) {
        console.error('Session error:', err);
        setError('Failed to initialize session: ' + err.message);
      } finally {
        setLoading(false);
        initializedRef.current = true;
        initializingRef.current = false;
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!initializedRef.current) return;
      
      if (event === 'SIGNED_IN') {
        setSession(session);
        if (session) {
          await Promise.all([
            fetchTickets(),
            fetchBoards(true),
            loadUserPreferences()
          ]);
        }
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        setTickets([]);
        setLoading(false);
      }
    });

    initSession();
    return () => subscription.unsubscribe();
  }, [fetchBoards, fetchTickets, loadUserPreferences]);

  // Initialize real-time collaboration when user logs in
  useEffect(() => {
    if (session && !loading) {
      initializeRealTime(session.user);
    }
    
    return () => {
      if (session) {
        goOffline();
      }
    };
  }, [session, loading, initializeRealTime, goOffline]);

  // Refetch tickets when view mode changes
  useEffect(() => {
    if (session && !loading) {
      fetchTickets();
    }
  }, [viewMode, session, loading, fetchTickets]);

  // Real-time ticket updates
  useEffect(() => {
    const handleTicketUpdate = (event) => {
      const { payload } = event.detail;
      
      if (payload.new && payload.new.user_id !== session?.user?.id) {
        console.log('🔄 Ticket updated by another user, refreshing...');
        fetchTickets();
      }
    };

    window.addEventListener('ticketUpdated', handleTicketUpdate);
    return () => window.removeEventListener('ticketUpdated', handleTicketUpdate);
  }, [session, fetchTickets]);

  // Cursor tracking
  useEffect(() => {
    if (!isConnected || !updateCursorPosition) return;

    const handleMouseMove = (e) => {
      if (cursorTimeoutRef.current) {
        clearTimeout(cursorTimeoutRef.current);
      }

      cursorTimeoutRef.current = setTimeout(() => {
        updateCursorPosition(e.clientX, e.clientY);
      }, 100);
    };

    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (cursorTimeoutRef.current) {
        clearTimeout(cursorTimeoutRef.current);
      }
    };
  }, [isConnected, updateCursorPosition]);

  // Drag scrolling effect
  useEffect(() => {
    const container = boardsRef.current;
    if (!container || loading) return;

    let scrollInterval = null;

    const handleDragOver = (e) => {
      const { clientX } = e;
      const rect = container.getBoundingClientRect();

      const threshold = 150;
      const scrollSpeed = 15;

      if (clientX - rect.left < threshold) {
        clearInterval(scrollInterval);
        scrollInterval = setInterval(() => {
          container.scrollLeft -= scrollSpeed;
        }, 16);
      } else if (rect.right - clientX < threshold) {
        clearInterval(scrollInterval);
        scrollInterval = setInterval(() => {
          container.scrollLeft += scrollSpeed;
        }, 16);
      } else {
        clearInterval(scrollInterval);
      }
    };

    const stopScrolling = () => clearInterval(scrollInterval);

    container.addEventListener('dragover', handleDragOver);
    container.addEventListener('dragleave', stopScrolling);
    container.addEventListener('drop', stopScrolling);

    return () => {
      container.removeEventListener('dragover', handleDragOver);
      container.removeEventListener('dragleave', stopScrolling);
      container.removeEventListener('drop', stopScrolling);
    };
  }, [loading]);

  // Board drag and drop handlers
  const handleBoardDragStart = (e, board) => {
    setDraggedBoard(board);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleBoardDragEnd = () => {
    setDraggedBoard(null);
    setDragOverBoard(null);
  };

  const handleBoardDragOver = (e, targetBoard) => {
    e.preventDefault();
    if (draggedBoard && draggedBoard.id !== targetBoard.id) {
      setDragOverBoard(targetBoard);
    }
  };

  const handleBoardDragLeave = () => {
    setDragOverBoard(null);
  };

  const handleBoardDrop = async (e, targetBoard) => {
    e.preventDefault();
    
    if (!draggedBoard || draggedBoard.id === targetBoard.id) {
      return;
    }

    const sourceIndex = boards.findIndex(b => b.id === draggedBoard.id);
    const targetIndex = boards.findIndex(b => b.id === targetBoard.id);

    if (sourceIndex !== -1 && targetIndex !== -1) {
      await reorderBoards(sourceIndex, targetIndex);
      
      await logActivity('reordered_boards', {
        boardName: draggedBoard.name,
        fromPosition: sourceIndex,
        toPosition: targetIndex
      });
    }

    setDraggedBoard(null);
    setDragOverBoard(null);
  };

  // UPDATED CSV UPLOAD WITH FLEXIBLE HEADER MATCHING
  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          const { data: { user } } = await supabase.auth.getUser();

          if (!user) {
            alert('You must be logged in to upload tickets!');
            return;
          }

          // 🔧 FLEXIBLE HEADER MAPPING - handles different cases
          const getFlexibleValue = (row, possibleHeaders) => {
            for (const header of possibleHeaders) {
              if (row[header] !== undefined) {
                return row[header];
              }
            }
            return undefined;
          };

          // Track import statistics
          let importStats = {
            processed: 0,
            created: 0,
            updated: 0,
            skipped: 0,
            errors: 0
          };

          console.log('🔄 Starting CSV import...');
          console.log('📊 Total rows to process:', results.data.length);

          for (const [index, row] of results.data.entries()) {
            try {
              importStats.processed++;
              
              // Extract and clean the issue key - flexible matching
              const issueKey = getFlexibleValue(row, ['Issue key', 'Issue Key', 'issue_key', 'IssueKey'])?.trim();
              
              if (!issueKey) {
                console.log(`⚠️ Row ${index + 1}: Skipping - no issue key`);
                importStats.skipped++;
                continue;
              }

              // Helper function to get non-empty value or null
              const getValueOrNull = (value) => {
                const cleaned = value?.trim();
                return cleaned && cleaned !== '' ? cleaned : null;
              };

              // Helper function to get float value or null
              const getFloatOrNull = (value) => {
                const cleaned = value?.toString().trim();
                if (!cleaned || cleaned === '') return null;
                const parsed = parseFloat(cleaned);
                return isNaN(parsed) ? null : parsed;
              };

              // 🔧 FLEXIBLE FIELD MAPPING - handles your lowercase headers
              const importedData = {
                summary: getValueOrNull(getFlexibleValue(row, ['Summary', 'summary'])),
                description: getValueOrNull(getFlexibleValue(row, ['Description', 'description'])),
                priority: getValueOrNull(getFlexibleValue(row, ['Priority', 'priority'])),
                status: getValueOrNull(getFlexibleValue(row, ['Status', 'status'])),
                assignee: getValueOrNull(getFlexibleValue(row, ['Assignee', 'assignee'])),
                component: getValueOrNull(getFlexibleValue(row, ['Component', 'component'])),
                labels: getValueOrNull(getFlexibleValue(row, ['Labels', 'labels'])),
                due_date: getValueOrNull(getFlexibleValue(row, ['Due Date', 'due_date', 'Due date'])),
                story_points: getValueOrNull(getFlexibleValue(row, ['Story Points', 'story_points', 'Story points'])),
                // ✅ FLEXIBLE CATEGORY MATCHING
                category: getValueOrNull(getFlexibleValue(row, ['Category', 'category'])),
                // ✅ FLEXIBLE EFFORT MATCHING - handles your "initial effort estimations" header
                initial_effort_estimations: getFloatOrNull(getFlexibleValue(row, [
                  'Initial Effort Estimations', 
                  'initial effort estimations',
                  'Initial effort estimations',
                  'initial_effort_estimations',
                  'effort',
                  'Effort'
                ])),
              };

              // Debug logging for new fields
              if (importedData.category || importedData.initial_effort_estimations) {
                console.log(`✨ Row ${index + 1} (${issueKey}): Category="${importedData.category}", Effort=${importedData.initial_effort_estimations}`);
              }

              // Handle board assignment - convert board name to board ID
              const sprintName = getValueOrNull(getFlexibleValue(row, ['Fix versions', 'Sprint', 'sprint', 'fix_versions']));
              if (sprintName) {
                const board = getBoardByName(sprintName);
                if (board) {
                  importedData.board_id = board.id;
                }
              }

              // Remove null values - we only want to update fields that have actual data
              const fieldsToUpdate = Object.fromEntries(
                Object.entries(importedData).filter(([key, value]) => value !== null)
              );

              // Check if ticket exists in database
              const { data: existingTicket, error: existingError } = await supabase
                .from('tickets')
                .select('*')
                .eq('issue_key', issueKey)
                .maybeSingle();

              if (existingError) {
                console.error(`❌ Row ${index + 1}: Error checking existing ticket:`, existingError);
                importStats.errors++;
                continue;
              }

              if (!existingTicket) {
                // CREATE NEW TICKET
                const newTicket = {
                  issue_key: issueKey,
                  user_id: user.id,
                  ...fieldsToUpdate
                };

                const { error: insertError } = await supabase
                  .from('tickets')
                  .insert(newTicket);

                if (insertError) {
                  console.error(`❌ Row ${index + 1}: Error creating ticket:`, insertError);
                  importStats.errors++;
                } else {
                  console.log(`✅ Row ${index + 1}: Created new ticket ${issueKey}`);
                  if (fieldsToUpdate.category || fieldsToUpdate.initial_effort_estimations) {
                    console.log(`   📝 Added - Category: "${fieldsToUpdate.category || 'N/A'}", Initial Effort: "${fieldsToUpdate.initial_effort_estimations || 'N/A'}"`);
                  }
                  importStats.created++;
                }
              } else {
                // UPDATE EXISTING TICKET (SELECTIVE MERGE)
                const mergedData = { ...existingTicket };
                const updatedFields = [];
                
                Object.entries(fieldsToUpdate).forEach(([field, importedValue]) => {
                  const existingValue = existingTicket[field];
                  
                  if (importedValue !== existingValue) {
                    mergedData[field] = importedValue;
                    updatedFields.push(`${field}: "${existingValue || 'NULL'}" → "${importedValue}"`);
                  }
                });

                if (updatedFields.length > 0) {
                  const { id, created_at, user_id, ...updateData } = mergedData;

                  const { error: updateError } = await supabase
                    .from('tickets')
                    .update(updateData)
                    .eq('issue_key', issueKey);

                  if (updateError) {
                    console.error(`❌ Row ${index + 1}: Error updating ticket:`, updateError);
                    importStats.errors++;
                  } else {
                    console.log(`🔄 Row ${index + 1}: Updated ticket ${issueKey}`);
                    console.log(`   📝 Changes: ${updatedFields.join(', ')}`);
                    importStats.updated++;
                  }
                } else {
                  console.log(`⏭️ Row ${index + 1}: No changes needed for ticket ${issueKey}`);
                  importStats.skipped++;
                }
              }
            } catch (rowError) {
              console.error(`❌ Row ${index + 1}: Unexpected error:`, rowError);
              importStats.errors++;
            }
          }

          // Refresh tickets from database
          await fetchTickets();

          // Log the import activity
          await logActivity('imported_tickets', {
            count: importStats.created + importStats.updated,
            created: importStats.created,
            updated: importStats.updated,
            fileName: file.name
          });

          // Show detailed import results
          const successMessage = `
📊 CSV Import Complete!

✅ Created: ${importStats.created} new tickets
🔄 Updated: ${importStats.updated} existing tickets  
⏭️ Skipped: ${importStats.skipped} tickets (no changes or no issue key)
❌ Errors: ${importStats.errors} failed operations
📈 Total Processed: ${importStats.processed} rows

✨ New Fields Supported:
   • Category (flexible header matching)
   • Initial Effort Estimations (flexible header matching)

${importStats.errors > 0 ? '\n⚠️ Check console for error details.' : ''}
          `.trim();

          alert(successMessage);
          console.log('📊 Import Statistics:', importStats);
        },
        error: (parseError) => {
          console.error('CSV parsing error:', parseError);
          alert('❌ Error parsing CSV file: ' + parseError.message);
        }
      });
    } catch (err) {
      console.error('CSV upload error:', err);
      alert('❌ Error uploading CSV file: ' + err.message);
    }
  };

  const handleTicketDrop = async (e, boardId) => {
    e.preventDefault();
    try {
      const ticketData = e.dataTransfer.getData('ticket');
      const reorderData = e.dataTransfer.getData('reorder-ticket');

      let droppedTicket;
      if (ticketData) {
        droppedTicket = JSON.parse(ticketData);
      } else if (reorderData) {
        droppedTicket = JSON.parse(reorderData);
      }

      if (!droppedTicket) return;

      // Get board info for activity logging
      const targetBoard = boardId ? getBoardById(boardId) : null;
      const sourceBoard = droppedTicket.board_id ? getBoardById(droppedTicket.board_id) : null;

      // Log the activity BEFORE making the change
      await logActivity('moved_ticket', {
        ticketKey: droppedTicket.issue_key,
        fromBoard: sourceBoard?.name || 'Backlog',
        toBoard: targetBoard?.name || 'Backlog',
        summary: droppedTicket.summary
      });

      // Update only board_id
      await supabase
        .from('tickets')
        .update({ board_id: boardId })
        .eq('issue_key', droppedTicket.issue_key);

      await fetchTickets();
    } catch (err) {
      console.error('Error dropping ticket:', err);
      setError('Failed to move ticket');
    }
  };

  const handleReorderTickets = async (draggedKey, targetKey, boardId) => {
    try {
      const boardTickets = tickets
        .filter((ticket) => ticket.board_id === boardId)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      const draggedIndex = boardTickets.findIndex((t) => t.issue_key === draggedKey);
      const targetIndex = boardTickets.findIndex((t) => t.issue_key === targetKey);

      if (draggedIndex === -1 || targetIndex === -1) return;

      const updatedBoardTickets = [...boardTickets];
      const [draggedTicket] = updatedBoardTickets.splice(draggedIndex, 1);
      updatedBoardTickets.splice(targetIndex, 0, draggedTicket);

      const updates = updatedBoardTickets.map((ticket, index) => ({
        ...ticket,
        order: index + 1,
      }));

      setTickets((prevTickets) => {
        const otherTickets = prevTickets.filter((t) => t.board_id !== boardId);
        return [...otherTickets, ...updates];
      });

      const updatesForDB = updates.map((ticket) =>
        supabase
          .from('tickets')
          .update({ order: ticket.order })
          .eq('issue_key', ticket.issue_key)
      );

      await Promise.all(updatesForDB);

      // Log the reorder activity
      const board = getBoardById(boardId);
      await logActivity('reordered_tickets', {
        ticketKey: draggedKey,
        boardName: board?.name || 'Unknown Board',
        fromPosition: draggedIndex,
        toPosition: targetIndex
      });
    } catch (err) {
      console.error('Error reordering tickets:', err);
      setError('Failed to reorder tickets');
    }
  };

  // Enhanced board creation with activity logging
  const handleCreateBoard = async (name, color = '#007bff', status = 'new') => {
    try {
      const isCollaborative = viewMode === 'collaborative';
      const newBoard = await createBoard(name, color, status, isCollaborative);
      await logActivity('created_board', {
        boardName: name,
        boardId: newBoard.id,
        status: status,
        mode: viewMode,
        isCollaborative
      });
    } catch (error) {
      console.error('Error creating board:', error);
    }
  };

  // Enhanced board update with activity logging
  const handleUpdateBoard = async (boardId, updates) => {
    try {
      const board = getBoardById(boardId);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!canEditBoard(board, user?.id)) {
        setError('You do not have permission to edit this board');
        return;
      }

      const updatedBoard = await updateBoard(boardId, updates);
      
      await logActivity('updated_board', {
        boardName: updatedBoard.name,
        boardId: boardId,
        changes: updates,
        mode: viewMode
      });
    } catch (error) {
      console.error('Error updating board:', error);
    }
  };

  // Enhanced board status update with activity logging
  const handleUpdateBoardStatus = async (boardId, status) => {
    try {
      const boardToUpdate = boards.find(b => b.id === boardId);
      await updateBoardStatus(boardId, status);
      await logActivity('updated_board_status', {
        boardName: boardToUpdate?.name,
        boardId: boardId,
        newStatus: status,
        oldStatus: boardToUpdate?.status
      });
      
      if (status === 'in_progress') {
        setTimeout(() => focusBoard(boardId), 300);
      }
    } catch (error) {
      console.error('Error updating board status:', error);
    }
  };

  // Enhanced board deletion with activity logging
  const handleDeleteBoard = async (boardId) => {
    try {
      const board = getBoardById(boardId);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!canEditBoard(board, user?.id)) {
        setError('You do not have permission to delete this board');
        return;
      }

      await deleteBoard(boardId);
      await logActivity('deleted_board', {
        boardName: board?.name,
        boardId: boardId,
        mode: viewMode
      });
    } catch (error) {
      console.error('Error deleting board:', error);
    }
  };

  // Memoized calculations
  const ticketsByBoard = useMemo(() => {
    return (boardId) => {
      return tickets
        .filter((ticket) => ticket.board_id === boardId)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    };
  }, [tickets]);

  // Calculate total effort for a board
  const getEffortSummary = useMemo(() => {
    return (boardId) => {
      const boardTickets = tickets.filter((ticket) => ticket.board_id === boardId);
      const totalEffort = boardTickets.reduce((sum, ticket) => {
        return sum + (ticket.initial_effort_estimations || 0);
      }, 0);
      
      return {
        totalDays: totalEffort,
        ticketCount: boardTickets.length,
        avgDays: boardTickets.length > 0 ? (totalEffort / boardTickets.length) : 0
      };
    };
  }, [tickets]);

  const unassignedTickets = useMemo(() => {
    return tickets.filter((ticket) => !ticket.board_id);
  }, [tickets]);

  if (loading) {
    return <LoadingSpinner message="Loading application..." />;
  }

  if (!session) {
    return <Login />;
  }

  return (
    <div className="app mode-transition" data-view-mode={viewMode} ref={containerRef}>
      {/* UPDATED HEADER - Clean Design */}
      <header style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        padding: '10px 20px', 
        backgroundColor: COLORS.PRIMARY, 
        color: 'white',
        height: '60px',
        boxSizing: 'border-box'
      }}>
        {/* Left Section - Logo and Title Only */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          flex: '0 0 auto'
        }}>
          <img
            src="https://greenlend.co.il/wp-content/uploads/2025/06/GreenlendLogo-White.png"
            alt="Logo"
            style={{ height: '32px', width: 'auto' }}
          />
          <h2 style={{ margin: 0, fontSize: '1.2rem' }}>Task Manager</h2>
        </div>

        {/* Center Section - Search Bar */}
        <div style={{ 
          flexGrow: 1, 
          display: 'flex', 
          justifyContent: 'center',
          // maxWidth: '500px',
          margin: '0 20px'
        }}>
          <SearchBar tickets={tickets} />
        </div>

        {/* Right Section - View Mode Toggle + User Menu */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          flex: '0 0 auto'
        }}>
          {/* View Mode Toggle - Moved next to avatar */}
          <ViewModeToggle 
            viewMode={viewMode}
            onViewModeChange={switchViewMode}
            onlineUsers={onlineUsers}
          />
          
          {/* User Menu with Connection Status Badge */}
          <UserMenu
            session={session}
            tickets={tickets}
            boards={boards}
            onSignOut={() => supabase.auth.signOut()}
            onCSVUpload={handleCSVUpload}
            isConnected={isConnected}
          />
        </div>
      </header>

      {/* Error Banner */}
      {(error || boardsError) && (
        <div className="error-banner">
          Error: {error || boardsError}
          <button 
            onClick={() => setError(null)}
            style={{ 
              marginLeft: '10px', 
              background: 'none', 
              border: 'none', 
              color: 'inherit', 
              cursor: 'pointer' 
            }}
          >
            ✖
          </button>
        </div>
      )}

      {/* Real-time collaboration components */}
      {session && isConnected && (
        <>
          <UserPresence 
            onlineUsers={onlineUsers} 
            currentUser={session.user}
            viewMode={viewMode}
          />
          <CollaborativeCursors
            onlineUsers={onlineUsers}
            currentUser={session.user}
            containerRef={containerRef}
            viewMode={viewMode}
          />
          {viewMode === 'collaborative' && (
            <>
              <LiveNotifications
                activities={recentActivities}
                currentUser={session.user}
              />
              <ActivityFeed
                activities={recentActivities}
                isOpen={activityFeedOpen}
                onToggle={() => setActivityFeedOpen(!activityFeedOpen)}
              />
            </>
          )}
        </>
      )}

      <div className="container">
        <Sidebar 
          tickets={unassignedTickets} 
          onDrop={(e) => handleTicketDrop(e, null)}
          loading={false}
        />
        
        <div className="boards" ref={boardsRef}>
          {boardsLoading ? (
            <LoadingSpinner message="Loading boards..." />
          ) : (
            <>
              {boards.map((board) => (
                <div
                  key={board.id}
                  className={`sprint-board-wrapper board-status-${board.status || 'new'}`}
                  onDragOver={(e) => handleBoardDragOver(e, board)}
                  onDragLeave={handleBoardDragLeave}
                  onDrop={(e) => handleBoardDrop(e, board)}
                  style={{
                    flex: '0 0 300px',
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '98%',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    backgroundColor: 'rgba(255, 255, 255, 0.85)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    transform: dragOverBoard?.id === board.id ? 'scale(1.02)' : 'scale(1)',
                    boxShadow: dragOverBoard?.id === board.id ? '0 8px 16px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                >
                  <DraggableBoardHeader
                    board={board}
                    ticketCount={ticketsByBoard(board.id).length}
                    effortSummary={getEffortSummary(board.id)}
                    onEdit={handleUpdateBoard}
                    onDelete={handleDeleteBoard}
                    onStatusChange={handleUpdateBoardStatus}
                    onDragStart={handleBoardDragStart}
                    onDragEnd={handleBoardDragEnd}
                    isDragging={draggedBoard?.id === board.id}
                    isOver={dragOverBoard?.id === board.id}
                  />
                  
                  <SprintBoard
                    key={board.id}
                    name={board.name}
                    board={board}
                    tickets={ticketsByBoard(board.id)}
                    onDrop={(e) => handleTicketDrop(e, board.id)}
                    onReorder={(draggedKey, targetKey) => handleReorderTickets(draggedKey, targetKey, board.id)}
                    loading={false}
                    hideHeader={true}
                  />
                </div>
              ))}
              
              <AddBoardButton 
                onAddBoard={handleCreateBoard}
                loading={boardsLoading}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;